/** SUNTRONIX ONE — Stock Report brand selector bar.
 *  Dynamic pills from actual inventory (brand_summary) + authorised Brand
 *  Master brands with zero stock (under "More"). View-only filter.
 */
import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

function BrandLogo({ label, logoUrl }) {
  const [broken, setBroken] = useState(false);
  if (logoUrl && !broken) {
    return <img src={logoUrl} alt={label} onError={() => setBroken(true)}
      className="h-4 w-4 object-contain rounded-sm" />;
  }
  return (
    <span className="h-4 w-4 grid place-items-center rounded-sm bg-slate-200 text-slate-600 text-[8px] font-bold">
      {initials(label)}
    </span>
  );
}

function Pill({ active, onClick, label, count, logoUrl, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={"shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors " +
        (active ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50")}>
      {logoUrl !== undefined && <BrandLogo label={label} logoUrl={logoUrl} />}
      <span>{label}</span>
      {count != null && (
        <span className={"tabular-nums text-[10px] px-1 rounded " +
          (active ? "bg-white/20" : "bg-slate-100 text-slate-500")}>{count}</span>
      )}
    </button>
  );
}

export function BrandBar({ brandSummary = [], allBrands = [], value, onChange }) {
  const [showMore, setShowMore] = useState(false);
  // Brands with stock (from summary) keyed by normalised key.
  const withStock = brandSummary;
  const stockKeys = useMemo(() => new Set(withStock.map((b) => b.key)), [withStock]);
  const logoByKey = useMemo(() => {
    const m = {};
    allBrands.forEach((b) => { m[b.key] = b.logo_url; });
    return m;
  }, [allBrands]);
  // Zero-stock authorised brands (only shown when "More" is expanded).
  const zeroStock = useMemo(
    () => allBrands.filter((b) => !stockKeys.has(b.key)),
    [allBrands, stockKeys]);

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="sr-brand-bar">
      <Pill active={!value} onClick={() => onChange("")} label="All Brands" testId="sr-brand-all" />
      {withStock.map((b) => (
        <Pill key={b.key} active={value === b.key} onClick={() => onChange(b.key)}
          label={b.label} count={b.qty} logoUrl={logoByKey[b.key] ?? null}
          testId={`sr-brand-${b.key}`} />
      ))}
      {showMore && zeroStock.map((b) => (
        <Pill key={b.key} active={value === b.key} onClick={() => onChange(b.key)}
          label={b.label} count={0} logoUrl={b.logo_url ?? null}
          testId={`sr-brand-${b.key}`} />
      ))}
      {zeroStock.length > 0 && (
        <button onClick={() => setShowMore((s) => !s)} data-testid="sr-brand-more"
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] text-slate-500 hover:text-slate-800">
          {showMore ? "Less" : `More (${zeroStock.length})`}
          <ChevronDown className={"h-3 w-3 transition-transform " + (showMore ? "rotate-180" : "")} />
        </button>
      )}
    </div>
  );
}

export function CategoryBar({ categorySummary = [], value, onChange }) {
  const [showMore, setShowMore] = useState(false);
  const primary = categorySummary.slice(0, 8);
  const rest = categorySummary.slice(8);
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="sr-category-bar">
      <Pill active={!value} onClick={() => onChange("")} label="All Categories" testId="sr-cat-all" />
      {primary.map((c) => (
        <Pill key={c.key} active={value === c.key} onClick={() => onChange(c.key)}
          label={c.label} count={c.qty} testId={`sr-cat-${c.key}`} />
      ))}
      {showMore && rest.map((c) => (
        <Pill key={c.key} active={value === c.key} onClick={() => onChange(c.key)}
          label={c.label} count={c.qty} testId={`sr-cat-${c.key}`} />
      ))}
      {rest.length > 0 && (
        <button onClick={() => setShowMore((s) => !s)} data-testid="sr-cat-more"
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] text-slate-500 hover:text-slate-800">
          {showMore ? "Less" : `More (${rest.length})`}
          <ChevronDown className={"h-3 w-3 transition-transform " + (showMore ? "rotate-180" : "")} />
        </button>
      )}
    </div>
  );
}

export function FilterChips({ chips = [], onClear, onClearAll }) {
  const active = chips.filter((c) => c.value);
  if (active.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="sr-filter-chips">
      {active.map((c) => (
        <span key={c.key} data-testid={`sr-chip-${c.key}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-300 text-[11px] text-slate-700">
          <span className="text-slate-400">{c.label}:</span> {c.display}
          <button onClick={() => onClear(c.key)} data-testid={`sr-chip-clear-${c.key}`}
            className="ml-0.5 text-slate-400 hover:text-slate-700">×</button>
        </span>
      ))}
      <button onClick={onClearAll} data-testid="sr-clear-all"
        className="text-[11px] text-slate-500 hover:text-slate-800 underline">Clear all</button>
    </div>
  );
}

